/**
 * Sprint 123 — Qué botón lleva cada tarjeta de la bandeja.
 *
 * ## Por qué la acción NO la decide la columna
 *
 * Es el error que una auditoría destructiva encontró en el diseño de tres columnas: si la columna
 * mandara, "Listo — entregar" tendría un único botón para todos, y ahí conviven un mostrador que
 * hay que **cobrar** y un domicilio en ruta que hay que **marcar entregado**. Peor: las llamadas no
 * son intercambiables entre canales —`marcarListo` **lanza excepción sobre un pedido pagado**, que
 * es el estado normal de un mostrador cobrado desde el Sprint 108—, así que un botón común
 * reventaría el primer día.
 *
 * La columna dice **dónde se dibuja**. Esta función dice **qué se puede hacer**, y mira el pedido
 * entero. Son dos preguntas distintas y por eso viven separadas.
 *
 * ## Una sola acción por tarjeta
 *
 * La disciplina viene del Sprint 111 y su razón está escrita allí: *"el operador nunca elige entre
 * dos botones: lee el único que hay"*. Quien atiende sola, con el cliente enfrente, no está para
 * comparar opciones.
 *
 * Módulo puro: se prueba entero sin pantalla ni base de datos.
 */

import {
  canalDelPedido,
  ejeOperativo,
  ejeCobro,
  type PedidoParaBandeja,
} from './bandejaPedidos';

/**
 * Lo que la tarjeta puede disparar. Cada una corresponde a una llamada que **ya existe** y que ya
 * está probada en su tablero de origen: la bandeja no inventa operaciones, sólo las reúne.
 */
export type AccionBandeja =
  /** `marcarEmpaquetado` — mostrador y domicilio. No toca `estado`, así que no mueve el dinero. */
  | 'empaquetar'
  /** `marcarListo` — sólo mesa: es el único canal sin `fechaEmpaquetado` (lo rechaza el servicio). */
  | 'marcar_listo'
  /** `marcarEntregadoMostrador` — "Se lo llevó". El servidor exige que ya exista el pago. */
  | 'entregar_mostrador'
  /** `marcarEntregadoMesa` — "Ya se lo llevaron a la mesa". */
  | 'entregar_mesa'
  /** `AsignarRepartidorModal` — "Entregar a la moto", con su ficha impresa. */
  | 'asignar_moto'
  /** `marcarEntregado` — el domicilio llegó a su destino. */
  | 'entregar_domicilio'
  /** `CobroModal` / `CobroTransferenciaModal` según el canal. */
  | 'cobrar'
  /**
   * `cerrarEntregadoPagado` — entregado y **ya pagado**: no cobra nada, sólo cierra.
   *
   * Existe porque su ausencia fue un callejón sin salida tres veces (Sprints 88, 100 y 102): la
   * acción obvia sobre una tarjeta que dice "falta cobrar" es cobrar, y `cobrar()` rebota con
   * *"este pedido ya tiene un pago registrado"*.
   */
  | 'dar_por_cerrado'
  /** `CotizarEnvioModal` — hay que preguntarle al grupo de motomandados cuánto cobran. */
  | 'cotizar_envio'
  /** Revisar un pedido que entró solo por el enlace: nadie lo ha visto todavía. */
  | 'revisar_del_enlace'
  /** Los que esperan el comprobante: prorrogar, recordar, confirmar o cancelar. */
  | 'esperando_pago';

export interface AccionDeTarjeta {
  accion: AccionBandeja;
  /** Lo que dice el botón. En el idioma del piso, no del modelo de datos. */
  etiqueta: string;
}

/**
 * La única acción de esta tarjeta.
 *
 * El orden de las preguntas importa: se resuelve **de atrás hacia adelante** en el flujo, porque un
 * pedido avanzado cumple también las condiciones de los anteriores.
 */
export function accionDeTarjeta(p: PedidoParaBandeja): AccionDeTarjeta {
  const canal = canalDelPedido(p);
  const op = ejeOperativo(p);
  const cobrado = ejeCobro(p) === 'cobrado';

  // 1. Lo que espera a un tercero: cada espera tiene su propia acción.
  if (op === 'esperando_cliente') {
    if (p.origen === 'enlace' && p.estado === 'abierto' && !p.fechaCotizacionEnvio) {
      return { accion: 'revisar_del_enlace', etiqueta: 'Revisar este pedido' };
    }
    if (!p.fechaCotizacionEnvio) {
      return { accion: 'cotizar_envio', etiqueta: 'Cotizar el envío' };
    }
    return { accion: 'esperando_pago', etiqueta: '¿Ya te llegó?' };
  }

  // 2. Entregado. Si ya se pagó, lo único que falta es cerrarlo — NO cobrarlo otra vez.
  if (op === 'entregado') {
    return cobrado
      ? { accion: 'dar_por_cerrado', etiqueta: 'Dar por cerrado' }
      : { accion: 'cobrar', etiqueta: 'Cobrar' };
  }

  // 3. En la calle: sólo queda confirmar que llegó.
  if (op === 'en_ruta') {
    return { accion: 'entregar_domicilio', etiqueta: 'Ya lo entregó' };
  }

  // 4. Listo. Aquí es donde la acción NO puede salir de la columna.
  if (op === 'listo') {
    if (canal === 'domicilio') {
      return { accion: 'asignar_moto', etiqueta: 'Entregar a la moto' };
    }
    if (canal === 'mostrador') {
      /**
       * La regla de la dueña, y la hace cumplir el servidor: *"no se lo entrego hasta que lo
       * pague"*. `marcarEntregadoMostrador` exige que exista el pago.
       */
      return cobrado
        ? { accion: 'entregar_mostrador', etiqueta: 'Se lo llevó' }
        : { accion: 'cobrar', etiqueta: 'Cobrar' };
    }
    return { accion: 'entregar_mesa', etiqueta: 'Ya se lo llevaron a la mesa' };
  }

  // 5. Preparando. Una mesa que pidió la cuenta ya no está preparando nada: quiere pagar.
  if (canal === 'mesa' && p.cuentaSolicitada === true) {
    return { accion: 'cobrar', etiqueta: 'Cobrar' };
  }
  /**
   * `marcarEmpaquetado` **rechaza los pedidos de mesa** (`tipo !== 'delivery' && !paraRecoger`), y
   * `marcarListo` **rechaza los pagados**. No es un capricho: son los dos ejes de DT-103 asomando.
   * Cada canal usa el que su servicio acepta.
   */
  return canal === 'mesa'
    ? { accion: 'marcar_listo', etiqueta: 'Ya está listo' }
    : { accion: 'empaquetar', etiqueta: 'Ya está listo' };
}
